import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiExternalLink, FiTrendingUp, FiDollarSign, FiUsers, FiBarChart2, FiPlus, FiMinus } from 'react-icons/fi';
import { useWallet } from '../hooks/useWallet';
import { useAuth } from '../hooks/useAuth';
import { useWalletContext } from '../context/WalletContext';
import { PublicKey, VersionedTransaction } from '@solana/web3.js';
import { failoverConnection as rpc } from '../utils/failoverConnection';
import { CONFIG } from '../config';
import styles from '../components/PerenaPool.module.css';
import { logger } from '../utils/logger';
import { recordStockPurchase, recordStockSale } from '../utils/stocksDb';

// Constants
const connection = rpc;
const USDC_MINT = new PublicKey('EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'); // USDC mint address

// Development logger - only logs in development environment
const devLog = (...args) => {
  logger.log(...args);
};

const StockDetail = () => {
  const { tokenAddress } = useParams();
  const navigate = useNavigate();
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [tokenBalance, setTokenBalance] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { wallet } = useWalletContext();
  const { email } = useAuth();

  // Show success notification
  const showSuccessNotification = (message) => {
    setSuccessMessage(message);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 5000); // Auto-hide after 5 seconds
  };

  // Close error toast
  const handleCloseErrorToast = () => setShowErrorToast(false);
  
  // Close success toast
  const handleCloseSuccessToast = () => setShowSuccessToast(false);

  // Success Toast component
  const SuccessToast = () => (
    <div className={styles.successToast}>
      <div className={styles.toastContent}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ 
            width: '24px', 
            height: '24px', 
            borderRadius: '50%', 
            background: '#10B981', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={{ color: '#065F46', fontWeight: 500 }}>{successMessage}</span>
        </div>
        <button 
          onClick={handleCloseSuccessToast}
          style={{
            background: 'none',
            border: 'none',
            color: '#065F46',
            cursor: 'pointer',
            padding: '4px',
            marginLeft: '1rem',
            fontSize: '1.25rem',
            lineHeight: 1
          }}
          aria-label="Dismiss notification"
        >
          &times;
        </button>
      </div>
    </div>
  );

  // Error Toast component
  const ErrorToast = () => (
    <div className={styles.errorToast}>
      <div className={styles.toastContent}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ 
            width: '24px', 
            height: '24px', 
            borderRadius: '50%', 
            background: '#EF4444', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span>{actionError}</span>
        </div>
        <button 
          onClick={handleCloseErrorToast}
          style={{
            background: 'none',
            border: 'none',
            color: '#9CA3AF',
            cursor: 'pointer',
            padding: '4px',
            marginLeft: '1rem',
            fontSize: '1.25rem',
            lineHeight: 1
          }}
          aria-label="Dismiss error"
        >
          &times;
        </button>
      </div>
    </div>
  );
  
  // Fetch user's token balance
  useEffect(() => {
    const fetchTokenBalance = async () => {
      if (!wallet?.publicKey || !tokenAddress) return;
      
      try {
        const response = await connection.getTokenAccountsByOwner(
          wallet.publicKey,
          { mint: new PublicKey(tokenAddress) }
        );
        
        if (response.value.length > 0) {
          const balance = response.value[0].account.data.parsed.info.tokenAmount.uiAmount;
          setTokenBalance(parseFloat(balance) || 0);
        } else {
          setTokenBalance(0);
        }
      } catch (err) {
        logger.error('Error fetching token balance:', err);
        setTokenBalance(0);
      }
    };

    if (wallet?.publicKey && tokenAddress) {
      fetchTokenBalance();
      // Refresh balance every 10 seconds
      const interval = setInterval(fetchTokenBalance, 10000);
      return () => clearInterval(interval);
    }
  }, [wallet, tokenAddress]);

  // function to handle the sale of stocks on chain
  const handleSell = async () => {
    logger.log('=== Starting stock sell process ===');
    
    if (!wallet?.publicKey) {
      const errorMsg = 'Error: Please connect your wallet first';
      logger.error(errorMsg);
      setActionError(errorMsg);
      setShowErrorToast(true);
      return;
    }

    try {
      setActionLoading(true);
      setActionError(null);
      
      const amount = parseFloat(withdrawAmount);
      if (isNaN(amount) || amount <= 0) {
        const errorMsg = 'Error: Please enter a valid amount';
        logger.error(errorMsg);
        throw new Error(errorMsg);
      }
      
      // Check if user has enough balance
      if (amount > tokenBalance) {
        const errorMsg = `Insufficient balance. You own ${tokenBalance} ${stock?.symbol || 'tokens'}`;
        logger.error(errorMsg);
        setActionError(errorMsg);
        setShowErrorToast(true);
        return;
      }

      if (!tokenAddress) {
        throw new Error('Stock token address not found');
      }

      logger.log('Stock sell parameters:', {
        tokenAddress,
        amount: amount.toString(),
        wallet: wallet.publicKey.toString(),
        network: CONFIG.SOLANA_NETWORK || 'mainnet-beta'
      });

      // Create Jupiter swap order (reverse of buy)
      const orderParams = new URLSearchParams({
        inputMint: tokenAddress, // Stock token as input
        outputMint: USDC_MINT.toBase58(), // USDC as output
        amount: Math.round(amount * 1e6).toString(), // Assuming 6 decimals for the stock token
        slippageBps: '100', // 1% slippage
        taker: wallet.publicKey.toBase58()
      });

      logger.log('Creating Jupiter swap order with params:', {
        ...Object.fromEntries(orderParams),
        fullUrl: `https://lite-api.jup.ag/ultra/v1/order?${orderParams.toString()}`
      });
      
      // Get the swap order from Jupiter
      const orderResponse = await fetch(`https://lite-api.jup.ag/ultra/v1/order?${orderParams.toString()}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (!orderResponse.ok) {
        const errorText = await orderResponse.text();
        throw new Error(errorText || 'Failed to create swap order');
      }

      const orderData = await orderResponse.json();
      if (!orderData.transaction || !orderData.requestId) {
        throw new Error('Invalid swap order response');
      }

      // Deserialize the transaction
      const transactionBytes = Uint8Array.from(atob(orderData.transaction), c => c.charCodeAt(0));
      const transaction = VersionedTransaction.deserialize(transactionBytes);
      
      // Sign the transaction with the user's wallet
      logger.log('Signing sell transaction...');
      const signedTransaction = await wallet.signTransaction(transaction);
      
      // Serialize the signed transaction
      const serializedTransaction = btoa(String.fromCharCode.apply(null, signedTransaction.serialize()));

      // Execute the swap
      logger.log('Executing sell swap...');
      const executeResponse = await fetch('https://lite-api.jup.ag/ultra/v1/execute', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          signedTransaction: serializedTransaction,
          requestId: orderData.requestId
        })
      });

      if (!executeResponse.ok) {
        const errorText = await executeResponse.text();
        throw new Error(errorText || 'Failed to execute sell swap');
      }

      const executeData = await executeResponse.json();
      
      if (executeData.status === 'Success') {
        // Wait for transaction confirmation
        logger.log('Waiting for sell transaction confirmation...');
        await connection.confirmTransaction(executeData.signature, 'confirmed');
        
        logger.log('Sell swap executed successfully:', {
          signature: executeData.signature,
          explorerUrl: `https://explorer.solana.com/tx/${executeData.signature}?cluster=${CONFIG.SOLANA_NETWORK || 'mainnet-beta'}`
        });
        
        // Close modal and reset form
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        
        // Show success message
        const successMsg = `✅ Successfully sold ${amount} ${stock.symbol || 'tokens'} for USDC!`;
        logger.log(successMsg);
        showSuccessNotification(`Successfully sold ${amount} ${stock.symbol || 'tokens'}`);
        
        // Record the sale in our database (best-effort)
        try {
          const walletAddressStr = wallet?.publicKey?.toBase58?.() || wallet?.publicKey?.toString?.();
          const currentPrice = Number(stock?.stockData?.price) || null;
          const tokenQty = Number(amount);
          const approxValue = currentPrice && Number.isFinite(tokenQty) ? tokenQty * currentPrice : null;
          await recordStockSale({
            userWalletAddress: walletAddressStr,
            tokenAddress,
            stockSymbol: stock?.symbol,
            stockName: stock?.name,
            amount: tokenQty,
            price: currentPrice,
            value: approxValue,
            txSignature: executeData.signature,
          });
        } catch (e) {
          logger.error('Failed to record stock sale:', e);
        }
        
        // Refresh stock data
        fetchStockDetail();
        
        return { success: true, signature: executeData.signature };
      } else {
        throw new Error(executeData.error || 'Sell execution failed');
      }
      
    } catch (err) {
      const errorMsg = `❌ Stock sell failed: ${err.message || 'Unknown error'}`;
      logger.error(errorMsg);
      logger.error('Error details:', err);
      
      setActionError(err.message || 'Failed to process stock sale');
      setShowErrorToast(true);
      
      return { success: false, error: err.message };
      
    } finally {
      logger.log('=== Stock sell process completed ===');
      setActionLoading(false);
    }
  };


  // fuunction to buy stocks on chain
  const handleBuy = async () => {
    console.log('=== Starting stock purchase process ===');
    
    if (!wallet?.publicKey) {
      const errorMsg = 'Error: Please connect your wallet first';
      logger.error(errorMsg);
      setActionError(errorMsg);
      setShowErrorToast(true);
      return;
    }

    try {
      setActionLoading(true);
      setActionError(null);
      
      const amount = parseFloat(depositAmount);
      if (isNaN(amount) || amount <= 0) {
        const errorMsg = 'Error: Please enter a valid amount';
        logger.error(errorMsg);
        throw new Error(errorMsg);
      }

      if (!tokenAddress) {
        throw new Error('Stock token address not found');
      }

      console.log('Stock purchase parameters:', {
        tokenAddress,
        amount: amount.toString(),
        wallet: wallet.publicKey.toString(),
        network: CONFIG.SOLANA_NETWORK || 'mainnet-beta'
      });

      // Convert amount to lamports (USDC has 6 decimals)
      const amountInLamports = Math.round(amount * 1e6).toString();
      
      // Create Jupiter swap order
      const orderParams = new URLSearchParams({
        inputMint: USDC_MINT.toBase58(),
        outputMint: tokenAddress, // Stock token address as output
        amount: amountInLamports,
        slippageBps: '100', // 1% slippage
        taker: wallet.publicKey.toBase58()
      });

      logger.log('Creating Jupiter swap order with params:', {
        ...Object.fromEntries(orderParams),
        fullUrl: `https://lite-api.jup.ag/ultra/v1/order?${orderParams.toString()}`
      });
      
      // Get the swap order from Jupiter
      const orderResponse = await fetch(`https://lite-api.jup.ag/ultra/v1/order?${orderParams.toString()}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (!orderResponse.ok) {
        const errorText = await orderResponse.text();
        throw new Error(errorText || 'Failed to create swap order');
      }

      const orderData = await orderResponse.json();
      if (!orderData.transaction || !orderData.requestId) {
        throw new Error('Invalid swap order response');
      }

      // Deserialize the transaction
      const transactionBytes = Uint8Array.from(atob(orderData.transaction), c => c.charCodeAt(0));
      const transaction = VersionedTransaction.deserialize(transactionBytes);
      
      // Sign the transaction with the user's wallet
      console.log('Signing transaction...');
      const signedTransaction = await wallet.signTransaction(transaction);
      
      // Serialize the signed transaction
      const serializedTransaction = btoa(String.fromCharCode.apply(null, signedTransaction.serialize()));

      // Execute the swap
      console.log('Executing swap...');
      const executeResponse = await fetch('https://lite-api.jup.ag/ultra/v1/execute', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          signedTransaction: serializedTransaction,
          requestId: orderData.requestId
        })
      });

      if (!executeResponse.ok) {
        const errorText = await executeResponse.text();
        throw new Error(errorText || 'Failed to execute swap');
      }

      const executeData = await executeResponse.json();
      
      if (executeData.status === 'Success') {
        // Wait for transaction confirmation
        console.log('Waiting for transaction confirmation...');
        await connection.confirmTransaction(executeData.signature, 'confirmed');
        
        console.log('Swap executed successfully:', {
          signature: executeData.signature,
          explorerUrl: `https://explorer.solana.com/tx/${executeData.signature}?cluster=${CONFIG.SOLANA_NETWORK || 'mainnet-beta'}`
        });
        
        // Close modal and reset form
        setShowDepositModal(false);
        setDepositAmount('');
        
        // Show success message
        const successMsg = `✅ Successfully purchased ${amount} USDC worth of ${stock.name || 'stock'}!`;
        logger.log(successMsg);
        showSuccessNotification(`Successfully bought ${amount} USDC of ${stock.symbol || 'stock'}`);
        
        // Record the purchase in our database (best-effort)
        try {
          const walletAddressStr = wallet?.publicKey?.toBase58?.() || wallet?.publicKey?.toString?.();
          const currentPrice = Number(stock?.stockData?.price) || null;
          const totalValue = Number(amount);
          await recordStockPurchase({
            userWalletAddress: walletAddressStr,
            tokenAddress,
            stockSymbol: stock?.symbol,
            stockName: stock?.name,
            amount: totalValue,
            price: currentPrice,
            value: totalValue,
            txSignature: executeData.signature,
          });
        } catch (e) {
          logger.error('Failed to record stock purchase:', e);
        }
        
        // Refresh stock data
        fetchStockDetail();
        
        return { success: true, signature: executeData.signature };
      } else {
        throw new Error(executeData.error || 'Swap execution failed');
      }
      
    } catch (err) {
      const errorMsg = `❌ Stock purchase failed: ${err.message || 'Unknown error'}`;
      logger.error(errorMsg);
      logger.error('Error details:', err);
      
      setActionError(err.message || 'Failed to process stock purchase');
      setShowErrorToast(true);
      
      return { success: false, error: err.message };
      
    } finally {
      console.log('=== Stock purchase process completed ===');
      setActionLoading(false);
    }
  };

  useEffect(() => {
    const fetchStockDetail = async () => {
      try {
        setLoading(true);
        devLog('Fetching stock details for token:', tokenAddress);
        
        if (!tokenAddress) {
          throw new Error('No token address provided');
        }
        
        const response = await fetch(`https://lite-api.jup.ag/tokens/v2/search?query=${tokenAddress}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        devLog('Received stock data:', data);
        
        if (data && data.length > 0) {
          setStock(data[0]);
        } else {
          setError('Stock not found');
          devLog('No stock data found for token:', tokenAddress);
        }
      } catch (err) {
        devLog('Error fetching stock details:', err);
        setError('Failed to load stock details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (tokenAddress) {
      fetchStockDetail();
    }
  }, [tokenAddress]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <div className="mt-4 text-gray-500">Loading stock details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <div className="mt-4">
                <button 
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FiArrowLeft className="mr-2 h-4 w-4" />
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!stock) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="mt-2 text-2xl font-bold text-gray-900">Stock not found</h2>
          <p className="mt-1 text-gray-500">We couldn't find the stock you're looking for.</p>
          <div className="mt-6">
            <button 
              onClick={() => navigate(-1)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FiArrowLeft className="mr-2 h-4 w-4" />
              Back to Stocks
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatNumber = (num) => {
    if (num === undefined || num === null) return 'N/A';
    const number = Number(num);
    if (isNaN(number)) return 'N/A';
    
    if (number >= 1e12) {
      return `$${(number / 1e12).toFixed(2)}T`;
    }
    if (number >= 1e9) {
      return `$${(number / 1e9).toFixed(2)}B`;
    }
    if (number >= 1e6) {
      return `$${(number / 1e6).toFixed(2)}M`;
    }
    if (number >= 1e3) {
      return `$${(number / 1e3).toFixed(1)}K`;
    }
    return `$${number.toFixed(2)}`;
  };
  
  const formatPercentage = (num) => {
    if (num === undefined || num === null) return 'N/A';
    const number = Number(num);
    if (isNaN(number)) return 'N/A';
    
    return `${number >= 0 ? '+' : ''}${number.toFixed(2)}%`;
  };
  
  const getPriceChangeClass = (change) => {
    if (change === undefined || change === null) return 'text-gray-500';
    return change >= 0 ? 'text-green-600' : 'text-red-600';
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatLargeNumber = (num) => {
    if (!num) return 'N/A';
    
    const value = Number(num);
    if (isNaN(value)) return 'N/A';
    
    if (value >= 1000000000) {
      return `$${(value / 1000000000).toFixed(2)}B`;
    } else if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(2)}K`;
    }
    return `$${value.toFixed(2)}`;
  };

  // Format price change with color and arrow
  const renderPriceChange = (change) => {
    if (change === undefined || change === null) return <span className="text-gray-500">N/A</span>;
    const isPositive = change >= 0;
    return (
      <span className={`flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? (
          <FiTrendingUp className="mr-1" />
        ) : (
          <FiTrendingUp className="mr-1 transform rotate-180" />
        )}
        {formatPercentage(change)}
      </span>
    );
  };

  // Handle deposit action
  const handleDeposit = async () => {
    try {
      setActionLoading(true);
      setActionError(null);
      
      const amount = parseFloat(depositAmount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid amount');
      }
      
      // TODO: Implement actual deposit logic here
      logger.log('Depositing:', amount, 'of', stock.symbol);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Close modal and reset form
      setShowDepositModal(false);
      setDepositAmount('');
      
    } catch (err) {
      logger.error('Deposit error:', err);
      setActionError(err.message || 'Failed to process deposit');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle withdraw action
  const handleWithdraw = async () => {
    try {
      setActionLoading(true);
      setActionError(null);
      
      const amount = parseFloat(withdrawAmount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid amount');
      }
      
      // TODO: Implement actual withdrawal logic here
      logger.log('Withdrawing:', amount, 'of', stock.symbol);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Close modal and reset form
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      
    } catch (err) {
      logger.error('Withdraw error:', err);
      setActionError(err.message || 'Failed to process withdrawal');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '1rem', 
        padding: '1rem 0.5rem', 
        borderBottom: '1px solid #e5e9f2', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <button 
            onClick={() => navigate(-1)}
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              padding: '0.5rem 1rem', 
              background: '#f3f4f6', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer', 
              fontSize: '0.95rem', 
              fontWeight: 500 
            }}
          >
            <FiArrowLeft size={16} /> Back
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {stock.icon && (
              <img 
                src={stock.icon} 
                alt={stock.name || 'Stock'} 
                style={{ 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '1px solid #e5e7eb'
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                }}
              />
            )}
            <h1 style={{ margin: 0, fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', marginBottom: '0.25rem' }}>
              {stock.name || 'Stock Detail'} {stock.symbol && `(${stock.symbol})`}
            </h1>
            {stock.id && (
              <a 
                href={`https://solscan.io/token/${stock.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#6b7280',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                View on Solscan <FiExternalLink size={14} />
              </a>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button 
            onClick={() => setShowDepositModal(true)}
            style={{
              padding: '0.5rem 1rem',
              background: '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              transition: 'background-color 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#4338ca'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#4f46e5'}
          >
            <FiPlus size={16} /> Buy
          </button>
          <button 
            onClick={() => setShowWithdrawModal(true)}
            style={{
              padding: '0.5rem 1rem',
              background: 'white',
              color: '#4f46e5',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              transition: 'background-color 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f9fafb'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
          >
            <FiMinus size={16} /> Sell
          </button>
        </div>
      </div>
      
      {/* Price and 24h Change */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '1.5rem',
        marginBottom: '1.5rem',
        padding: '1rem',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Price</div>
          <div className={styles.metricValue} style={{ fontSize: '2rem', fontWeight: 600 }}>
            {stock.stockData?.price ? `$${Number(stock.stockData.price).toFixed(2)}` : 'N/A'}
          </div>
        </div>
        {stock.stats24h?.priceChange !== undefined && (
          <div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>24h Change</div>
            <div style={{ 
              color: stock.stats24h.priceChange >= 0 ? '#10b981' : '#ef4444',
              fontWeight: 500,
              fontSize: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem'
            }}>
              {stock.stats24h.priceChange >= 0 ? '▲' : '▼'} {Math.abs(stock.stats24h.priceChange).toFixed(2)}%
            </div>
          </div>
        )}
      </div>

      <div className={styles.metricsGrid}>
        <div className={styles.metricCard}>
          <h3>Market Cap</h3>
          <div className={styles.metricValue}>
            {stock.stockData?.mcap ? `${formatNumber(stock.stockData.mcap, 0)}` : 'N/A'}
          </div>
        </div>
        <div className={styles.metricCard}>
          <h3>24h Volume</h3>
          <div className={styles.metricValue}>
            {stock.stats24h?.buyVolume && stock.stats24h?.sellVolume 
              ? `${formatLargeNumber(Number(stock.stats24h.buyVolume) + Number(stock.stats24h.sellVolume))}`
              : 'N/A'}
          </div>
        </div>
        <div className={styles.metricCard}>
          <h3>Holders</h3>
          <div className={styles.metricValue}>{stock.holderCount?.toLocaleString() || 'N/A'}</div>
        </div>
        <div className={styles.metricCard}>
          <h3>Liquidity</h3>
          <div className={styles.metricValue}>
            {stock.liquidity ? `${formatNumber(stock.liquidity)}` : 'N/A'}
          </div>
        </div>
      </div>

      <div className={styles.actionCard} style={{ marginBottom: '1.5rem' }}>
        <h3>Performance</h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
          gap: '1.5rem',
          marginTop: '1rem'
        }}>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>1h Change</div>
            <div style={{ fontWeight: 500, fontSize: '1.1rem' }}>
              {renderPriceChange(stock.stats1h?.priceChange)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>6h Change</div>
            <div style={{ fontWeight: 500, fontSize: '1.1rem' }}>
              {renderPriceChange(stock.stats6h?.priceChange)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>24h Change</div>
            <div style={{ fontWeight: 500, fontSize: '1.1rem' }}>
              {renderPriceChange(stock.stats24h?.priceChange)}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.actionCard}>
        <h3>Details</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <div>
            <div className={styles.balanceItem}><span>Circulating Supply</span> <strong>{formatNumber(stock.circSupply)}</strong></div>
            <div className={styles.balanceItem}><span>Total Supply</span> <strong>{formatNumber(stock.totalSupply)}</strong></div>
            <div className={styles.balanceItem} style={{ borderBottom: 'none' }}>
              <span>Token Address</span>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <strong style={{ fontFamily: 'monospace' }}>{stock.id.substring(0, 4)}...{stock.id.slice(-4)}</strong>
                <button onClick={() => navigator.clipboard.writeText(stock.id)} title="Copy" style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: '0.5rem' }}>⎘</button>
              </div>
            </div>
          </div>
          <div>
            <h4>Tags</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {stock.tags?.map(tag => <span key={tag} style={{ background: '#eef2ff', color: '#4f46e5', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.8rem' }}>{tag}</span>)}
            </div>
          </div>
        </div>
      </div>

      {/* Buy Modal */}
      {showDepositModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Buy {stock?.symbol || 'Token'}</h3>
              <button 
                onClick={() => setShowDepositModal(false)}
                className={styles.closeButton}
                disabled={actionLoading}
              >
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label htmlFor="buy-amount">Amount in USDC</label>
                <div className={styles.inputWithSuffix}>
                  <input
                    id="buy-amount"
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    placeholder="0.00"
                    disabled={actionLoading}
                    min="0"
                    step="0.01"
                  />
                  <span className={styles.inputSuffix}>USDC</span>
                </div>
                {stock?.price && (
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    ~{(depositAmount / stock.price).toFixed(4)} {stock.symbol}
                  </div>
                )}
              </div>
              {actionError && <div className={styles.errorText}>{actionError}</div>}
            </div>
            <div className={styles.modalFooter}>
              <button
                onClick={() => setShowDepositModal(false)}
                className={styles.secondaryButton}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleBuy}
                className={styles.primaryButton}
                disabled={actionLoading || !depositAmount}
                style={{ backgroundColor: '#4f46e5' }}
              >
                {actionLoading ? 'Processing...' : 'Buy'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sell Modal */}
      {showWithdrawModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Sell {stock?.symbol || 'Token'}</h3>
              <button 
                onClick={() => setShowWithdrawModal(false)}
                className={styles.closeButton}
                disabled={actionLoading}
              >
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label htmlFor="sell-amount">Amount to Sell</label>
                <div className={styles.balanceText} style={{ fontSize: '0.875rem', marginBottom: '0.5rem', color: '#6b7280' }}>
                  Balance: {tokenBalance.toLocaleString(undefined, { maximumFractionDigits: 4 })} {stock?.symbol || 'tokens'}
                </div>
                <div className={styles.inputWithSuffix}>
                  <input
                    id="sell-amount"
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow numbers and decimal points, and validate against max balance
                      if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= tokenBalance)) {
                        setWithdrawAmount(value);
                      }
                    }}
                    placeholder="0.00"
                    min="0"
                    max={tokenBalance}
                    step="any"
                    className={styles.input}
                    disabled={actionLoading}
                    onKeyDown={(e) => {
                      // Prevent typing 'e', '+', '-', etc.
                      if (['e', 'E', '+', '-'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <span className={styles.inputSuffix}>
                    <button 
                      type="button" 
                      className={styles.maxButton}
                      onClick={() => setWithdrawAmount(tokenBalance.toString())}
                      disabled={actionLoading || tokenBalance <= 0}
                    >
                      MAX
                    </button>
                    <span>{stock?.symbol || 'TOK'}</span>
                  </span>
                </div>
                {withdrawAmount && parseFloat(withdrawAmount) > tokenBalance && (
                  <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    Exceeds available balance
                  </div>
                )}
                {stock?.price && (
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    ~${(withdrawAmount * stock.price).toFixed(2)} USDC
                  </div>
                )}
              </div>
              {actionError && <div className={styles.errorText}>{actionError}</div>}
            </div>
            <div className={styles.modalFooter}>
              <button
                onClick={() => setShowWithdrawModal(false)}
                className={styles.secondaryButton}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSell}
                className={styles.primaryButton}
                disabled={actionLoading || !withdrawAmount || parseFloat(withdrawAmount) > tokenBalance || parseFloat(withdrawAmount) <= 0}
                style={{ backgroundColor: '#ef4444' }}
              >
                {actionLoading ? 'Processing...' : 'Sell'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast Notifications */}
      {showErrorToast && <ErrorToast />}
      {showSuccessToast && <SuccessToast />}
    </div>
  );
};

export default StockDetail;
